import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { NAV_LINKS } from "@/data/content";

const Header = () => {
    const [open, setOpen] = useState(false);
    const { pathname } = useLocation();

    useEffect(() => {
        setOpen(false);
    }, [pathname]);

    return (
        <>
            <a
                href="#main-content"
                data-testid="skip-to-content"
                className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[60] focus:bg-bronze focus:px-4 focus:py-2 focus:text-cream"
            >
                Skip to main content
            </a>
            <header
                data-testid="site-header"
                className="fixed inset-x-0 top-0 z-50 border-b border-bronze/20 bg-charcoal/95 backdrop-blur-md"
            >
                <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6 sm:h-24 sm:px-12 lg:px-20">
                    <Link
                        to="/"
                        data-testid="brand-logo"
                        aria-label="Kavraj Spaces — home"
                        className="flex items-center gap-3"
                    >
                        <img
                            src="/kavraj-emblem.png"
                            alt=""
                            className="h-11 w-auto sm:h-12"
                        />
                        <span className="flex flex-col leading-none">
                            <span className="font-serif text-lg tracking-[0.22em] text-cream sm:text-xl">
                                KAVRAJ
                            </span>
                            <span className="mt-1 text-[0.55rem] font-medium tracking-[0.45em] text-bronze-light">
                                SPACES
                            </span>
                        </span>
                    </Link>

                    <nav aria-label="Primary" className="hidden items-center gap-5 xl:flex xl:gap-7">
                        {NAV_LINKS.map((link) => (
                            <NavLink
                                key={link.path}
                                to={link.path}
                                data-testid={link.testid}
                                className={({ isActive }) =>
                                    `relative whitespace-nowrap py-2 text-[0.78rem] font-medium tracking-[0.1em] transition-colors duration-300 after:absolute after:bottom-0 after:left-0 after:h-px after:bg-bronze-light after:transition-all after:duration-300 ${
                                        isActive
                                            ? "text-bronze-light after:w-full"
                                            : "text-cream/75 after:w-0 hover:text-cream hover:after:w-full"
                                    }`
                                }
                            >
                                {link.name}
                            </NavLink>
                        ))}
                        <Link
                            to="/contact"
                            data-testid="header-cta-discuss"
                            className="ml-2 whitespace-nowrap bg-bronze px-4 py-2.5 text-[0.78rem] font-semibold tracking-[0.06em] text-cream transition-colors duration-300 hover:bg-bronze-light"
                        >
                            Discuss a Requirement
                        </Link>
                    </nav>

                    <button
                        type="button"
                        data-testid="mobile-menu-toggle"
                        aria-label={open ? "Close menu" : "Open menu"}
                        aria-expanded={open}
                        onClick={() => setOpen((v) => !v)}
                        className="p-2 text-cream transition-colors duration-300 hover:text-bronze-light xl:hidden"
                    >
                        {open ? <X size={26} aria-hidden="true" /> : <Menu size={26} aria-hidden="true" />}
                    </button>
                </div>

                {open && (
                    <nav
                        aria-label="Mobile"
                        data-testid="mobile-menu"
                        className="border-t border-cream/10 bg-charcoal px-6 pb-8 pt-4 xl:hidden"
                    >
                        <ul className="flex flex-col">
                            {NAV_LINKS.map((link) => (
                                <li key={link.path}>
                                    <NavLink
                                        to={link.path}
                                        data-testid={`mobile-${link.testid}`}
                                        className={({ isActive }) =>
                                            `block border-b border-cream/5 py-4 font-serif text-2xl transition-colors duration-300 ${
                                                isActive ? "text-bronze-light" : "text-cream hover:text-bronze-light"
                                            }`
                                        }
                                    >
                                        {link.name}
                                    </NavLink>
                                </li>
                            ))}
                        </ul>
                        <Link
                            to="/contact"
                            data-testid="mobile-header-cta-discuss"
                            className="mt-6 inline-flex w-full items-center justify-center bg-bronze px-5 py-4 text-sm font-semibold tracking-wide text-cream transition-colors duration-300 hover:bg-bronze-light"
                        >
                            Discuss a Requirement
                        </Link>
                    </nav>
                )}
            </header>
        </>
    );
};

export default Header;
